/**
 * AccommodationStrip — the horizontal band above the timeline holding accommodation bookings, one row of
 * night cells spanning the whole trip. Each booking is absolutely positioned by night index using the same
 * per-day width factors as the grid columns (so it stays aligned under the day headers). Empty night slots
 * become drop targets while dragging; a booked cell can be dragged to another night, resized across nights,
 * or opened. Nights are the gaps between days, so there are `dayCount - 1` of them.
 */
import { useRef } from 'react';
import type { TFunction } from 'i18next';

import { PlaceholderType } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { TYPE_COLOR, TypeIcon } from '../model';
import { readDrag, writeDrag, type PalettePayload } from '../model';
import type { BoardPart } from '../model';
import { spanLabel } from './stripGeometry';
import { useStripResize } from './useStripResize';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface AccommodationStripProps {
  t: TFunction;
  dayCount: number;
  weights: number[];
  bookings: BoardPart[];
  readOnly: boolean;
  /** True only while a compatible drag (accommodation booking or accommodation placeholder) is active. */
  dropActive: boolean;
  onOpen: (uid: string) => void;
  onDropPalette: (payload: PalettePayload, nightIdx: number) => void;
  onMove: (uid: string, nightIdx: number) => void;
  onResize: (uid: string, nights: number) => void;
  onDragChange: (dragging: boolean) => void;
}

export function AccommodationStrip({
  t,
  dayCount,
  weights,
  bookings,
  readOnly,
  dropActive,
  onOpen,
  onDropPalette,
  onMove,
  onResize,
  onDragChange,
}: AccommodationStripProps) {
  const totalNights = dayCount - 1;
  const rowRef = useRef<HTMLDivElement>(null);
  const total = weights.reduce((a, b) => a + b, 0);
  const startResize = useStripResize(rowRef, onResize);

  if (totalNights <= 0) return <div className="tp-accom-row" ref={rowRef} />;

  // A night sits between two day columns; its cell is centered on that gap, inset a little on each side.
  const cum = (n: number) => weights.slice(0, n).reduce((a, b) => a + b, 0);
  const nightStart = (n: number) => ((cum(n) + weights[n] / 2) / total) * 100;
  const nightEnd = (n: number) => ((cum(n + 1) + weights[n + 1] / 2) / total) * 100;
  const nightInset = (n: number) => (weights[n] / total / 24) * 100;

  const sorted = [...bookings].sort((a, b) => a.day - b.day);

  const handleResizeStart = (event: React.MouseEvent, booking: BoardPart) => {
    // Don't overlap the next booking: cap the span at the gap to it (or the trip end).
    const next = sorted.find((o) => o.day > booking.day);
    const maxNights = (next ? next.day : totalNights) - booking.day;
    startResize(event, booking, maxNights, 0);
  };

  const cells: React.ReactNode[] = [];
  let cursor = 0;
  const pushEmpty = (n: number) => {
    const inset = nightInset(n);
    cells.push(
      <div
        key={`empty-${n}`}
        className={cn('tp-accom-cell tp-accom-cell--empty', dropActive && 'is-droppable')}
        style={{
          left: `calc(${nightStart(n)}% + ${inset}%)`,
          width: `calc(${nightEnd(n) - nightStart(n)}% - ${inset * 2}%)`,
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add('is-over');
        }}
        onDragLeave={(e) => e.currentTarget.classList.remove('is-over')}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('is-over');
          const payload = readDrag(e);
          if (!payload) return;
          if (payload.source === 'accom-strip') onMove(payload.uid, n);
          // Only accommodation placeholders may create a booking here (non-accom drags never reach this).
          else if (payload.source === 'palette' && payload.type === PlaceholderType.Accommodation)
            onDropPalette(payload, n);
        }}
      >
        <div className="tp-accom-empty-text">{t('costScheme.dropAccommodation')}</div>
      </div>
    );
  };

  sorted.forEach((booking) => {
    for (let n = cursor; n < booking.day; n += 1) pushEmpty(n);
    const span = Math.max(1, booking.span);
    const insetL = nightInset(booking.day);
    const insetR = nightInset(booking.day + span - 1);
    const style: StyleVars = {
      left: `calc(${nightStart(booking.day)}% + ${insetL}%)`,
      width: `calc(${nightEnd(booking.day + span - 1) - nightStart(booking.day)}% - ${insetL + insetR}%)`,
      '--act-color': TYPE_COLOR[booking.type],
    };
    const hint = [booking.facilitySubTypeName, booking.accommodationCategoryName]
      .filter(Boolean)
      .join(' · ');
    cells.push(
      <div
        key={`b-${booking.uid}`}
        className="tp-accom-cell tp-accom-cell--booked"
        draggable={!readOnly}
        onDragStart={(e) => {
          writeDrag(e, { source: 'accom-strip', uid: booking.uid }, 'move');
          onDragChange(true);
        }}
        onDragEnd={() => onDragChange(false)}
        style={style}
        onClick={() => onOpen(booking.uid)}
      >
        <div className="tp-act-accent" aria-hidden="true" />
        <div className="tp-act-body">
          <div className="tp-act-icon">
            <TypeIcon type={booking.type} size={12} />
          </div>
          <div className="tp-act-text">
            <div className="tp-act-label">{booking.name}</div>
            <div className="tp-act-time">
              {spanLabel(t, booking.day, span, 'night')}
              {hint ? <span className="tp-act-dur"> · {hint}</span> : null}
            </div>
          </div>
        </div>
        {readOnly ? null : (
          <div
            className="tp-accom-resize"
            title={t('costScheme.stretchNights')}
            onMouseDown={(e) => handleResizeStart(e, booking)}
          >
            <span className="tp-accom-resize-grip" aria-hidden="true" />
          </div>
        )}
      </div>
    );
    cursor = booking.day + span;
  });
  for (let n = cursor; n < totalNights; n += 1) pushEmpty(n);

  return (
    <div className="tp-accom-row" ref={rowRef}>
      {cells}
    </div>
  );
}
